import React, { useState } from 'react';
import { useTrackFutureValue } from '../hooks/useTrackFutureValue';
import { notifyUser } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';

// --- 1. Define the ExplanationModal component ---
const ExplanationModal = ({ isOpen, onClose }) => {
  const modalWidth = useModalWidth();
  if (!isOpen) return null;

  // Note: Increased zIndex to 1100 to ensure it appears on top of the main calculator modal (1000)
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100, // Higher than FutureValueCalculatorModal's zIndex (1000)
    }} onClick={onClose}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 24,
        width: '90%',
        maxWidth: modalWidth,
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 20, color: '#00704A' }}>Parameter Explanations</h2>

        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#00704A', marginBottom: 8 }}>Initial Lump Sum</h3>
          <p style={{ color: '#555', lineHeight: 1.5 }}>
            Enter the amount you're starting with in your pension account. Example: if you already have GHS 10,000 saved, enter that here.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#00704A', marginBottom: 8 }}>Monthly Contribution</h3>
          <p style={{ color: '#555', lineHeight: 1.5 }}>
            Enter how much you plan to contribute every month going forward. This helps us to project how your pension will grow with regular savings.
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#00704A', marginBottom: 8 }}>Annual Interest Rate</h3>
          <p style={{ color: '#555', lineHeight: 1.5 }}>
            Enter the expected yearly interest rate (as a percentage). Example: if you expect fund to grow by 10% per year, enter "10".
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <h3 style={{ color: '#00704A', marginBottom: 8 }}>Years Of Retirement</h3>
          <p style={{ color: '#555', lineHeight: 1.5 }}>
            Enter how many years you plan to contribute before retirement. This helps us calculate how long your money will grow.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            className="btn btn-primary"
            style={{
              padding: '10px 20px',
              fontSize: 16,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
// --- End ExplanationModal definition ---


const FutureValueCalculatorModal = ({ isOpen, onClose }) => {
  const modalWidth = useModalWidth();
  const [initialLumpSum, setInitialLumpSum] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [yearsOfRetirement, setYearsOfRetirement] = useState('');
  const [annualInterestRate, setAnnualInterestRate] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  // State for the Explanation Modal
  const [showExplanation, setShowExplanation] = useState(false);
  const [futureValue, setFutureValue] = useState('');

  const trackMutation = useTrackFutureValue();

  const omkTextFieldStyles = React.useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  }), []);

  const calculateFutureValue = () => {
    const A = parseFloat(initialLumpSum) || 0;
    const P = parseFloat(monthlyContribution) || 0;
    const n = parseFloat(yearsOfRetirement) || 0;
    const rAnnual = parseFloat(annualInterestRate) / 100 || 0;

    if (A < 0 || P < 0 || n <= 0 || rAnnual < 0) {
      notifyUser('error', 'Please enter valid positive numbers for all fields.', 'Validation Error');
      return;
    }

    const rMonthly = rAnnual / 12;
    const months = n * 12;

    // Future Value of Lump Sum (A)
    let fv = A * Math.pow(1 + rMonthly, months);
    
    // Future Value of Monthly Contributions (P) - Each contribution compounded for the full period
    if (P > 0) {
      fv += P * months * Math.pow(1 + rMonthly, months);
    }

    const totalContributions = A + P * months;
    const interestEarned = fv - totalContributions;

    const calculatedFutureValue = fv.toFixed(2);

    setFutureValue(calculatedFutureValue);
    setBreakdown({
      totalContributions: totalContributions.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
    });

    // Track the calculator usage
    trackMutation.mutate({
      inputs: [initialLumpSum, monthlyContribution, yearsOfRetirement, annualInterestRate],
      result: calculatedFutureValue,
    });
  };

  if (!isOpen) return null;

  return (
    // Fragment used to hold both the Calculator Modal and the Explanation Modal
    <>
      {/* --- Main Calculator Modal Structure --- */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }} onClick={onClose}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          width: '90%',
          maxWidth: modalWidth,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }} onClick={(e) => e.stopPropagation()}>
          <h2 style={{ marginBottom: 20, color: '#00704A' }}>Future Value Calculator</h2>

          {/* ... (Input fields, all onClick/onInput handlers corrected) ... */}
          
          <div style={{ marginBottom: 16 }}>
            <omk-text-field
              label="Initial Lump Sum (GHS)"
              name="initialLumpSum"
              type="number"
              value={initialLumpSum}
              onInput={(e) => setInitialLumpSum(e.target.value)}
              style={omkTextFieldStyles}
              placeholder="Enter initial lump sum"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <omk-text-field
              label="Monthly Contribution (GHS)"
              name="monthlyContribution"
              type="number"
              value={monthlyContribution}
              onInput={(e) => setMonthlyContribution(e.target.value)}
              style={omkTextFieldStyles}
              placeholder="Enter monthly contribution"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <omk-text-field
              label="Years Of Retirement"
              name="yearsOfRetirement"
              type="number"
              value={yearsOfRetirement}
              onInput={(e) => setYearsOfRetirement(e.target.value)}
              style={omkTextFieldStyles}
              placeholder="Enter years of retirement"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <omk-text-field
              label="Annual Interest Rate (%)"
              name="annualInterestRate"
              type="number"
              value={annualInterestRate}
              onInput={(e) => setAnnualInterestRate(e.target.value)}
              style={omkTextFieldStyles}
              placeholder="Enter annual interest rate"
            />
          </div>

          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <span
              style={{ color: '#00704A', cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}
              onClick={() => setShowExplanation(true)}
            >
              See Explanation Of Parameters
            </span>
          </div>

          <button
            onClick={calculateFutureValue}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 16,
            }}
          >
            Calculate Future Value
          </button>

          {futureValue && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f8f0', borderRadius: 8 }}>
              <strong>Future Value: GHS {futureValue}</strong>
            </div>
          )}

          {breakdown && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8, color: '#00704A' }}>Breakdown</h3>
              <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Total Contributions: GHS {breakdown.totalContributions}</strong>
                </div>
                <div>
                  <strong>Interest Earned: GHS {breakdown.interestEarned}</strong>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              className="btn btn-white"
              style={{
                flex: 1,
                padding: '12px',
                fontSize: 16,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* --- 2. Render the ExplanationModal outside the main structure --- */}
      <ExplanationModal 
        isOpen={showExplanation} 
        onClose={() => setShowExplanation(false)} 
      />
    </>
  );
};

export default FutureValueCalculatorModal;